import React, { useCallback, useEffect, useState } from 'react';
import { FaCheck, FaClock, FaUserCircle } from 'react-icons/fa';
import { Modal, ModalBody, ModalFooter } from 'reactstrap';
import { compareAsc, differenceInMinutes } from 'date-fns';
import api from '~/services/api';
import { ReactComponent as WarningIcon } from '~/assets/img/warning.svg';
import { ReactComponent as WarningIconRed } from '~/assets/img/warningRed.svg';
import PhoneString from '~/services/formatPhoneString';
import formatCompleteDate from '~/services/formatCompleteDate';
import { Button, IconIfood, IconRefresh, Spinner } from 'takeat-design-system-ui-kit';
import toast from 'react-hot-toast';
import { useAuth } from '~/context/AuthContext';
import OrderItem from './OrderItem';

import {
  BuyerInfo,
  ConfirmButton,
  Container,
  CustomBadge,
  Footer,
  Header,
  IfoodTurboBadge,
  Main,
  Orders,
} from './styles';

import ChannelTag from './ChannelTag';

function OrderCard({
  item,
  baskets,
  setBaskets,
  kds,
  kds_index,
  disableButtonSubmit,
  setDisableButtonSubmit,
}) {
  const { user } = useAuth();

  const buttonStatus =
    item?.status === 'pending' ? 'Iniciar Preparo' : 'Finalizar';

  function getBadgeText(status) {
    if (status === 'pending') {
      return 'NOVO';
    }

    if (status === 'doing') {
      return 'EM PREPARO';
    }

    if (status === 'finished') {
      return 'FINALIZADO';
    }

    if (status === 'canceled') {
      return 'CANCELADO';
    }

    return false;
  }

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChangeStatus = useCallback(
    async (order_status, id, order_basket_id) => {
      const newStatus = order_status === 'pending' ? 'doing' : 'finished';
      const previousStatus = order_status;

      // Optimistic update - atualiza UI imediatamente
      setIsSubmitting(true);
      setDisableButtonSubmit([order_basket_id]);

      const newBaskets = [...baskets];
      const basketFind = newBaskets?.findIndex((item) => item?.id === id);

      if (basketFind !== -1) {
        newBaskets[basketFind].status = newStatus;
      }
      setBaskets(newBaskets);

      try {
        await api.put(`/restaurants/kds/basket/status/${item?.id}`, {
          status: newStatus,
        });
      } catch (err) {
        // Reverte para o status anterior em caso de erro
        const revertedBaskets = [...baskets];
        const revertIndex = revertedBaskets?.findIndex((item) => item?.id === id);

        if (revertIndex !== -1) {
          revertedBaskets[revertIndex].status = previousStatus;
        }
        setBaskets(revertedBaskets);
        toast.error('Erro ao atualizar comanda. Verifique conexão ou integrações.');
      } finally {
        setIsSubmitting(false);
        setDisableButtonSubmit([]);
      }
    },
    [item, baskets, setBaskets, setDisableButtonSubmit]
  );

  const [isLate, setIsLate] = useState([]);
  const [isVeryLate, setIsVeryLate] = useState([]);

  const verifyIfIsLate = useCallback(() => {
    const currentTime = new Date();

    if (item?.scheduled_to) {
      const scheduledTime = new Date(item.scheduled_to);

      if (compareAsc(currentTime, scheduledTime) === 1) {
        setIsLate([item.id]);
      }
    } else if (item?.kds_orders) {
      const arrivedAtTime = new Date(item.arrived_at);

      item.kds_orders.forEach((kds) => {
        const timeDifference = differenceInMinutes(currentTime, arrivedAtTime);

        if (timeDifference > kds?.average_time) {
          setIsVeryLate((old) => [...old, kds.id]);
        } else if (timeDifference > kds?.average_time / 2) {
          setIsLate((old) => [...old, kds.id]);
        }
      });
    }
  }, [item]);

  useEffect(() => {
    verifyIfIsLate();
  }, [verifyIfIsLate]);

  useEffect(() => {
    const verifyIfProductIsLate = setInterval(verifyIfIsLate, 60000);
    return () => {
      clearInterval(verifyIfProductIsLate);
    };
  }, [isLate, isVeryLate, item, verifyIfIsLate]);

  const [modalChangeStep, setModalChangeStep] = useState(false);

  function toggleModalChangeStep() {
    setModalChangeStep(!modalChangeStep);
  }

  async function handleChangeStepStatus() {
    try {
      await api.put(`/restaurants/kds/basket/previous-step/${item?.id}`);
      toast.success('Item retornado para etapa anterior');
    } catch (err) {
      if (err.response.data.errorType === 'kds_previous_step_not_found') {
        toast.error('Etapa anterior não encontrada');
      } else if (err.response.data.errorType === 'kds_invalid_operation') {
        toast.error('Operação inválida. A próxima etapa já está em andamento.');
      } else {
        toast.error('Erro ao voltar para a etapa anterior');
      }
      console.log(err);
    }
  }

  return (
    <Container>
      <Modal isOpen={modalChangeStep} toggle={() => setModalChangeStep(false)}>
        <ModalBody style={{ display: 'flex', gap: '10px' }}>
          {item.next_kds_id ? (
            <div>
              <WarningIcon />
            </div>
          ) : (
            <div>
              <WarningIconRed />
            </div>
          )}
          <div>
            {item.status === 'finished' ? (
              <p
                style={{
                  fontSize: '18px',
                  fontWeight: 'bold',
                  lineHeight: '24px',
                }}
              >
                Tem certeza que deseja voltar para preparo?
              </p>
            ) : (
              <p
                style={{
                  fontSize: '18px',
                  fontWeight: 'bold',
                  lineHeight: '24px',
                }}
              >
                Tem certeza que deseja voltar para a etapa anterior?
              </p>
            )}

            {!item.next_kds_id && item.status === 'finished' && (
              <p>
                Ao confirmar, o envio da mensagem do despacho no Whatsapp e o
                despacho do pedido no iFood não é revertido.
              </p>
            )}
          </div>
        </ModalBody>
        <ModalFooter style={{ justifyContent: 'end' }}>
          <div>
            <Button
              customColor="#fff"
              style={{ color: '#222' }}
              onClick={toggleModalChangeStep}
            >
              Cancelar
            </Button>
            <Button customColor="#2EC9B7" onClick={handleChangeStepStatus}>
              Confirmar
            </Button>
          </div>
        </ModalFooter>
      </Modal>
      <Main>
        <Header status={item.status}>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <span style={{ fontWeight: 'bold', fontSize: 18 }}>
              #{item?.attendance_password || item?.basket_key}
              {/* {['self-service', 'pos'].includes(item?.table.table_type) ? (
                <>
                  {item?.attendance_password
                    ? item.attendance_password
                    : item.basket_key}
                </>
              ) : (
                <>
                  {item?.table.table_type === 'delivery'
                    ? item?.attendance_password
                    : item?.basket_key}
                </>
              )} */}
            </span>

            <CustomBadge pill status={item?.status}>
              {getBadgeText(item?.status)}
            </CustomBadge>
          </div>

          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            {['self-service', 'pos'].includes(item?.table.table_type) && (
              // <div style={{ display: 'flex', flexDirection: 'column' }}>
              <>
                <span style={{ fontWeight: 'bold' }}>Totem</span>
                {item?.with_withdrawal ? (
                  <span style={{ fontWeight: 500 }}>Para viagem</span>
                ) : (
                  <span style={{ fontWeight: 500 }}>Comer no local</span>
                )}
                {/* </div> */}
              </>
            )}

            {item?.table.table_type === 'drive-thru' && <span>Drive-Thru</span>}

            {item?.table.table_type === 'command' ? (
              <p style={{ fontWeight: 600, fontSize: 20 }}>
                {' '}
                Comanda {item.table.table_number} / Mesa{' '}
                {item.command_table_number || 'n/a'}
              </p>
            ) : (
              !['self-service', 'pos'].includes(item?.table.table_type) && (
                <div style={{ display: 'flex', gap: 4 }}>
                  <ChannelTag
                    channel={item?.channel}
                    table={item?.table}
                    isWithdrawal={item?.with_withdrawal}
                  />

                  {item?.delivery_mode === 'TURBO' && (
                    <IfoodTurboBadge>
                      <IconIfood
                        style={{
                          fill: "#C8131B",

                        }}
                        fontSize={22}
                      />
                      <span style={{ display: 'flex', gap: 2 }}>
                        <span
                          style={{ fontWeight: 700, fontSize: 12 }}

                        >
                          Turbo
                        </span>
                        <span>
                          iFood
                        </span>
                      </span>
                    </IfoodTurboBadge>
                  )}

                </div>

              )
            )}
            {item?.table.table_type === 'balcony' &&
              (item?.with_withdrawal ? (
                <span style={{ fontWeight: 500 }}>Para viagem</span>
              ) : (
                <span style={{ fontWeight: 500 }}>Comer no local</span>
              ))}

            {user?.delivery_info?.use_attendance_password_asc &&
              ['delivery', 'drive-thru'].includes(item?.table.table_type) && (
                <span>Nº #{item.basket_key}</span>
              )}
          </div>
        </Header>
        <BuyerInfo
        // isProductLate={
        //   isLate.includes(item?.id) &&
        //   (item?.status === 'doing' || item?.status === 'pending')
        // }
        >
          <span>
            <FaUserCircle />{' '}
            <span>
              {item?.buyer
                ? item?.buyer?.name
                  ? item?.buyer?.name
                  : PhoneString(item?.buyer?.phone)
                : item?.waiter?.name}
            </span>
          </span>
          {item?.scheduled_to && (
            <span>Agendado para: {formatCompleteDate(item?.scheduled_to)}</span>
          )}

          {!item?.scheduled_to && (
            <span>
              <FaClock /> <span>{formatCompleteDate(item?.arrived_at)}</span>
            </span>
          )}
        </BuyerInfo>
        <Orders>
          {item &&
            item?.kds_orders &&
            item?.kds_orders.length > 0 &&
            item?.kds_orders
              .sort((a, b) => {
                if (a.canceled_at) {
                  return 1;
                }
                if (b.canceled_at) {
                  return -1;
                }
                return a.id - b.id;
              })
              .map((product, index) => (
                <OrderItem
                  product={product}
                  basket={item}
                  baskets={baskets}
                  setBaskets={setBaskets}
                  isLate={
                    isLate.includes(product?.id) &&
                    (item?.status === 'doing' || item?.status === 'pending')
                  }
                  isVeryLate={
                    isVeryLate.includes(product?.id) &&
                    (item?.status === 'doing' || item?.status === 'pending')
                  }
                  key={product.id}
                  kds={kds}
                />
              ))}
        </Orders>
      </Main>

      {(item?.status === 'pending' || item?.status === 'doing') && (
        <Footer>
          {kds?.parent_kds_id && kds_index !== 0 && (
            <Button
              style={{
                width: 112,
                height: 48,
                display: 'flex',
                background: '#F6F6F6',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: 8,
              }}
              onClick={toggleModalChangeStep}
              icon={
                <IconRefresh
                  fontSize={22}
                  style={{ fill: '#545454', transform: 'scaleX(-1)' }}
                />
              }
            >
              <span
                style={{
                  fontSize: 12,
                  color: '#545454',
                }}
              >
                Voltar etapa
              </span>
            </Button>
          )}

          <ConfirmButton
            type="button"
            status={item?.status}
            onClick={() =>
              handleChangeStatus(item?.status, item?.id, item?.order_basket_id)
            }
            disabled={
              isSubmitting ||
              disableButtonSubmit.includes(item?.order_basket_id)
            }
          >
            {isSubmitting ? (
              <span
                style={{
                  width: '100%',
                  display: 'flex',
                  justifyContent: 'center',
                }}
              >
                <Spinner color="#fff" size={22} />
              </span>
            ) : (
              <span>
                {item?.status === 'doing' && (
                  <FaCheck style={{ marginRight: 8 }} />
                )}
                {buttonStatus}
              </span>
            )}
          </ConfirmButton>
        </Footer>
      )}

      {kds?.parent_kds_id && item?.status === 'finished' && kds_index !== 0 && (
        <Footer>
          <Button
            style={{
              width: '100%',
              height: 48,
              display: 'flex',
              background: '#F6F6F6',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: 8,
            }}
            onClick={toggleModalChangeStep}
            icon={
              <IconRefresh
                fontSize={22}
                style={{ fill: '#545454', transform: 'scaleX(-1)' }}
              />
            }
          >
            <span
              style={{
                fontSize: 16,
                color: '#545454',
              }}
            >
              Voltar para preparo
            </span>
          </Button>
        </Footer>
      )}
    </Container>
  );
}

export default OrderCard;
